import { Vector2 } from "three";
import { Kante, Polygon, PolygonKante, RahmenDef } from "./types";
import {
  clipPolygonByHalfplane,
  lineIntersection,
  MultiPoly,
  normalizePolygon,
  pairRingToVec,
  PolyRings,
  samePoint,
  snapMultiPoly,
  snapPoly,
  vecToPairRing,
} from "./geometry";
import polygonClipping from "polygon-clipping";
import { convexHull, cross, segmentIntersection } from "oc-threejs/Maths2";
import { v2 } from "oc-threejs/short3";

// Zuerst: erzeugen einer Struktur pro Kante mit den vier Eckpunkten (a_pos/a_neg/b_pos/b_neg)
type EdgeData = {
  k: Kante;
  a: Vector2;
  b: Vector2;
  dir: Vector2;
  normal: Vector2;
  distA_pos: number;
  distA_neg: number;
  distB_pos: number;
  distB_neg: number;
  a_pos: Vector2;
  a_neg: Vector2;
  b_pos: Vector2;
  b_neg: Vector2;
};

type PolyData = { poly: Polygon; prio: number; k: Kante };

function buildEdgeData(kanten: Kante[]) {
  const edgeData: EdgeData[] = [];
  for (const k of kanten) {
    const a = k.start.position;
    const b = k.end.position;

    const dir = v2().subVectors(b, a);
    dir.normalize(); // unit direction along edge
    // normal = dir um 90° gegen den Uhrzeigersinn rotieren (Basisnormal)
    const baseNormal = v2()
      .copy(dir)
      .rotateAround(v2(0, 0), Math.PI / 2); // unit normal (left of dir)

    let distPos: number;
    let distNeg: number;
    // Deterministische Regel: für Außenkanten wird die linke Seite der Kante (wenn man von start->end schaut) als innen betrachtet.
    // Das bedeutet: baseNormal (links von dir) ist die Innenrichtung.
    // Falls Sie stattdessen die rechte Seite als innen möchten, invertieren Sie baseNormal hier.
    let usedNormal = baseNormal;

    // Bestimme mittels neuem Flag aussen, ob Kante zur Außenkontur gehört
    if (k.aussen) {
      // feste Regel: linke Seite = innen
      usedNormal = baseNormal;
      distPos = k.breite;
      distNeg = 0;
    } else {
      // fallback: nicht Außenkante -> zentriert auf die Kantenbreite
      usedNormal = baseNormal;
      const half = k.breite / 2;
      distPos = half;
      distNeg = -half;
    }

    const a_pos = v2(a).addScaledVector(usedNormal, distPos);
    const a_neg = v2(a).addScaledVector(usedNormal, distNeg);
    const b_pos = v2(b).addScaledVector(usedNormal, distPos);
    const b_neg = v2(b).addScaledVector(usedNormal, distNeg);

    edgeData.push({
      k,
      a,
      b,
      dir,
      normal: usedNormal,
      distA_pos: distPos,
      distA_neg: distNeg,
      distB_pos: distPos,
      distB_neg: distNeg,
      a_pos,
      a_neg,
      b_pos,
      b_neg,
    });
  }
  return edgeData;
}

function incidentEdges(edgeData: EdgeData[], nodePos: Vector2) {
  const res: EdgeData[] = [];
  for (const e of edgeData) {
    if (samePoint(e.a, nodePos) || samePoint(e.b, nodePos)) res.push(e);
  }
  return res;
}

function applyMiterCuts(knoten: RahmenDef["knoten"], edgeData: EdgeData[]) {
  for (const node of knoten) {
    if (!node.gehrung) continue;
    const inc = incidentEdges(edgeData, node.position);
    if (inc.length !== 2) continue;
    const e1 = inc[0];
    const e2 = inc[1];

    // Für beide Seiten (pos/neg) Schnittpunkte berechnen und Eckpunkte ersetzen
    for (const side of ["pos", "neg"] as const) {
      // Bestimme Basispunkte und Richtungen (Linien durch node + normal*dist, Richtung entlang edge away from node)
      const setup = (e: EdgeData) => {
        const isA = samePoint(e.a, node.position);
        const dist = isA
          ? side === "pos"
            ? e.distA_pos
            : e.distA_neg
          : side === "pos"
            ? e.distB_pos
            : e.distB_neg;
        const base = v2(node.position).addScaledVector(e.normal, dist);
        // Richtung: falls node == a, richtung ist dir (weg von a), sonst -dir (weg von b zurück)
        const dir = isA ? e.dir.clone() : v2().copy(e.dir).multiplyScalar(-1);
        return { base, dir, e };
      };

      const s1 = setup(e1);
      const s2 = setup(e2);
      const ip = lineIntersection(s1.base, s1.dir, s2.base, s2.dir);
      if (ip) {
        // Setze die jeweilige Ecke am Endpunkt auf den Schnittpunkt
        // Für e1
        if (samePoint(e1.a, node.position)) {
          if (side === "pos") e1.a_pos = ip;
          else e1.a_neg = ip;
        } else {
          if (side === "pos") e1.b_pos = ip;
          else e1.b_neg = ip;
        }
        // Für e2
        if (samePoint(e2.a, node.position)) {
          if (side === "pos") e2.a_pos = ip;
          else e2.a_neg = ip;
        } else {
          if (side === "pos") e2.b_pos = ip;
          else e2.b_neg = ip;
        }
      }
    }
  }
}

function buildPolyData(edgeData: EdgeData[]) {
  const polyData: PolyData[] = [];
  for (const e of edgeData) {
    const quad = normalizePolygon([e.a_pos, e.a_neg, e.b_neg, e.b_pos]);
    polyData.push({ poly: quad, prio: e.k.prio ?? 0, k: e.k });
  }
  return polyData;
}

function clipPolygons(polyData: PolyData[]) {
  for (let i = 0; i < polyData.length; i++) {
    for (let j = 0; j < polyData.length; j++) {
      if (i === j) continue;
      if (polyData[i].prio === polyData[j].prio) continue;
      const A = polyData[i];
      const B = polyData[j];
      // baue Segmentlisten
      const segsA: Array<[Vector2, Vector2]> = [];
      const segsB: Array<[Vector2, Vector2]> = [];
      for (let s = 0; s < A.poly.length; s++)
        segsA.push([A.poly[s], A.poly[(s + 1) % A.poly.length]]);
      for (let s = 0; s < B.poly.length; s++)
        segsB.push([B.poly[s], B.poly[(s + 1) % B.poly.length]]);

      for (const sa of segsA) {
        for (const sb of segsB) {
          const epsSide = 1e-9;
          const inter = segmentIntersection(sa, sb, epsSide);
          if (inter?.type !== "X") continue; // nur echte Kreuzungspunkte zulassen

          // bestimme höher- und nieder-prio polygon und das relevante Segment der höher-prio
          let higher: PolyData, lower: PolyData, segHigher: [Vector2, Vector2];
          if (A.prio > B.prio) {
            higher = A;
            lower = B;
            segHigher = sa;
          } else {
            higher = B;
            lower = A;
            segHigher = sb;
          }

          // falls lower bereits leer, nichts zu tun
          if (!lower.poly || lower.poly.length === 0) continue;

          // Bestimme Richtung und auf welcher Seite das higher-Polygon liegt
          const ph1 = segHigher[0];
          const ph2 = segHigher[1];
          const segDir = v2().subVectors(ph2, ph1);
          // Robustere Bestimmung der zu behaltenden Seite:
          // Prüfe auf welcher Seite der Schwerpunkt des lower-polygons liegt und behalte diese Seite.
          const lowerCenter = lower.poly
            .reduce((acc, p) => acc.add(v2(p)), v2(0, 0))
            .multiplyScalar(1 / Math.max(1, lower.poly.length));
          const lowerCross = cross(
            segDir.x,
            segDir.y,
            lowerCenter.x - ph1.x,
            lowerCenter.y - ph1.y
          );
          const lowerSide = lowerCross > epsSide ? 1 : lowerCross < -epsSide ? -1 : 0; // 1 = left, -1 = right, 0 = ambiguous
          let keepSide: 1 | -1;
          if (lowerSide !== 0) {
            // Behalte die Seite, auf der das lower-Polygon liegt (wir schneiden den Teil weg, der mit higher überlappt)
            keepSide = lowerSide;
          } else {
            // Fallback: benutze die entgegengesetzte Seite des higher-polygons (wie vorher)
            const centerHigher = higher.poly
              .reduce((acc, p) => acc.add(v2(p)), v2(0, 0))
              .multiplyScalar(1 / Math.max(1, higher.poly.length));
            const crossVal = cross(
              segDir.x,
              segDir.y,
              centerHigher.x - ph1.x,
              centerHigher.y - ph1.y
            );
            const higherSide = crossVal > epsSide ? 1 : crossVal < -epsSide ? -1 : 0;
            keepSide = higherSide === -1 ? 1 : -1;
          }
          // Falls die Richtung zu klein ist (degenerat), skippen
          if (segDir.length() < 1e-12) continue;

          const clipped = clipPolygonByHalfplane(lower.poly, ph1, ph2, keepSide);
          if (clipped && clipped.length >= 3) {
            lower.poly = clipped;
          } else {
            // skip clipping if it would remove the polygon entirely (safer fallback)
          }
        }
      }
    }
  }
}

export function generateFrame(frame: RahmenDef): PolygonKante[] {
  const { knoten, kanten } = frame;
  const polygonKanten: PolygonKante[] = [];

  const edgeData = buildEdgeData(kanten);

  applyMiterCuts(knoten, edgeData);

  const polyData = buildPolyData(edgeData);
  clipPolygons(polyData);

  // Ergebnis sammeln (nur gültige Polygone) – für Darstellung
  for (const pd of polyData) {
    if (pd.poly && pd.poly.length >= 3)
      polygonKanten.push({ polygon: normalizePolygon(pd.poly), kante: pd.k });
  }

  return polygonKanten;
}

export function generateFenster(frame: RahmenDef, polygonKanten: PolygonKante[]): Polygon[] {
  // 1) Außenpolygon ermitteln: konvexe Hülle aller vorhandenen Rahmen-Polygone
  let outer: Polygon | null = null;
  const allFramePts = polygonKanten.flatMap((pk) => pk.polygon);
  if (allFramePts.length >= 3) {
    outer = normalizePolygon(convexHull(allFramePts));
  } else {
    // Fallback: konvexe Hülle aus aussen-Kanten-Endpunkten, sonst alle Knoten
    const outerPts = frame.kanten
      .filter((k) => k.aussen)
      .flatMap((k) => [k.start.position, k.end.position]);
    const pts = outerPts.length >= 3 ? outerPts : frame.knoten?.map((k) => k.position) ?? [];
    if (pts.length >= 3) outer = normalizePolygon(convexHull(pts));
  }
  // Falls outer immer noch nicht valide, gib nichts zurück
  if (!outer || outer.length < 3) return [];

  // 2) Unterschied: Außenpolygon minus alle Rahmenpolygone (zuvor Union + Snap)
  const grid = 1e-6; // Rastergröße (in px) – sehr fein, genügt zum Einrasten numerischer Reste
  const subject: PolyRings = snapPoly([vecToPairRing(outer)], grid);
  // Sammle alle gültigen Rahmen-Polygone als MultiPolygons mit je einem Ring und snappe sie
  const clips: MultiPoly = snapMultiPoly(
    polygonKanten
      .filter((pk) => pk.polygon && pk.polygon.length >= 3)
      .map((pk) => [vecToPairRing(pk.polygon)]),
    grid
  );

  let diff: MultiPoly;
  if (clips.length === 0) {
    diff = [subject];
  } else {
    // Zuerst Union der Rahmen bilden (stabiler), dann Differenz
    // @ts-ignore
    const unionFrames = polygonClipping.union(...clips) as MultiPoly;
    const unionSnapped = snapMultiPoly(unionFrames, grid);
    diff = polygonClipping.difference(subject as any, unionSnapped as any) as MultiPoly;
  }

  if (!diff || diff.length === 0) return [];

  // 3) Ergebnis-Multipolygone in Vector2-Polygone konvertieren; nur äußere Ringe zurückgeben
  const result: Polygon[] = [];
  for (const poly of diff) {
    if (!poly || poly.length === 0) continue;
    const outerRing = poly[0];
    const ringVec = normalizePolygon(pairRingToVec(outerRing));
    if (ringVec.length >= 3) result.push(ringVec);
  }
  return result;
}
